import React from 'react';
import IconElements from '@/assets/icons/icon-elements.svg?react';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import IconPlus from '@/assets/icons/icon-plus-sign.svg?react';
import IconDelete from '@/assets/icons/icon-delete.svg?react';
import { Pencil } from 'lucide-react';
import clsx from 'clsx';
import { useImageManager } from '@/hooks/useImageManager';
import type { AssetResponse } from '@lp/lib-asset-api';

interface ImageSocialPreviewProps {
  variant?: 'default';
  imageSrc: string | null;
  imageId: string | null;
  socialUrl?: string;
  socialPageTitle?: string;
  socialDescription?: string;
  onImageSelect: (image: AssetResponse) => void;
  onImageRemove: () => void;
}

export const ImageSocialPreview: React.FC<ImageSocialPreviewProps> = ({
  imageSrc,
  imageId,
  socialUrl,
  socialPageTitle,
  socialDescription,
  onImageSelect,
  onImageRemove,
}) => {
  const { openImageManager } = useImageManager();

  const handleAddClick = () => {
    openImageManager({ onSelectImage: onImageSelect });
  };

  const handleReplaceClick = () => {
    openImageManager({
      onSelectImage: onImageSelect,
      activeImageId: imageId ?? undefined,
    });
  };

  return (
    <div className={clsx('flex max-w-[216px] flex-col gap-2 rounded-md bg-neutral-50 p-2')}>
      <div
        className={clsx(
          'group bg-neutral-10 relative inline-flex h-31 w-50 cursor-pointer flex-col items-center justify-center gap-2.5 rounded outline outline-offset-[-1px] outline-indigo-500 outline-dashed',
        )}
      >
        {imageSrc ? (
          <div className="flex h-20 w-30 items-center justify-center overflow-hidden rounded">
            <img src={imageSrc} alt="Uploaded Preview" className="h-full w-full object-cover" />
          </div>
        ) : (
          <>
            <div className="flex h-full w-full items-center justify-center overflow-hidden rounded bg-indigo-50">
              <div className="flex flex-col items-center justify-center">
                <IconElements className="h-6 w-6 text-[#927FEF]" />
                <div className="font-semiboldleading-tight pt-[5px] text-center text-xs text-indigo-500">
                  Choose Image
                </div>
                <div className="pt-[2px] text-center text-[10px] font-medium text-neutral-500">
                  Recommended: 1200px x 630px
                </div>
              </div>
            </div>
          </>
        )}

        {/* Image Not Added */}
        {!imageSrc && (
          <div className="bg-neutral-10 absolute top-0 left-0 z-10 flex h-full w-full gap-2 rounded p-1 opacity-0 outline outline-offset-[-1px] outline-neutral-100 transition-all group-hover:opacity-100">
            <div className="flex h-[116px] w-[208px] items-center justify-center rounded bg-indigo-50 outline outline-offset-[-1px] outline-indigo-500 outline-dashed">
              <PrimaryButton
                size="sm"
                text="Choose Image"
                icon={IconPlus}
                onClick={handleAddClick}
              />
            </div>
          </div>
        )}

        {/* Image Added */}
        {imageSrc && (
          <div className="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center gap-2 rounded border border-indigo-500 bg-indigo-50 opacity-0 transition-all group-hover:opacity-100">
            <PrimaryButton text="Edit" size="sm" icon={Pencil} onClick={handleReplaceClick} />
            <SecondaryButton text="Remove" size="sm" icon={IconDelete} onClick={onImageRemove} />
          </div>
        )}
      </div>
      <div className="inline-flex flex-col items-start justify-start gap-0.5 self-stretch">
        <p className="max-w-full truncate text-xs font-medium text-indigo-500">
          {socialUrl || 'MY.EXAMPLE.COM'}
        </p>
        {socialPageTitle && (
          <p className="max-w-full truncate text-[10px] font-semibold text-neutral-700">
            {socialPageTitle}
          </p>
        )}
        <p className="line-clamp-2 max-w-full text-[10px] font-medium break-all text-neutral-500">
          {socialDescription || 'Description appears here. Edit below.'}
        </p>
      </div>
    </div>
  );
};
